import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  DocumentData,
  QueryDocumentSnapshot,
  Query,
  limit,
  Timestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { Resource } from '../types/resource';

const convertResource = (doc: QueryDocumentSnapshot<DocumentData>): Resource => {
  const data = doc.data();
  
  // Convertir les timestamps Firestore en objets Date
  let createdAt = new Date();
  if (data.createdAt) {
    if (data.createdAt instanceof Timestamp) {
      createdAt = data.createdAt.toDate();
    } else if (data.createdAt.seconds) {
      createdAt = new Date(data.createdAt.seconds * 1000);
    }
  }
  
  let updatedAt = new Date();
  if (data.updatedAt) {
    if (data.updatedAt instanceof Timestamp) {
      updatedAt = data.updatedAt.toDate();
    } else if (data.updatedAt.seconds) {
      updatedAt = new Date(data.updatedAt.seconds * 1000);
    }
  }
  
  return {
    id: doc.id,
    title: data.title || 'Sans titre',
    description: data.description || '',
    type: data.type || 'fiche',
    level: data.level || 'terminale',
    theme: data.theme || 'economie',
    chapter: data.chapter || '',
    pdfUrl: data.pdfUrl || '',
    downloads: data.downloads || 0,
    createdAt,
    updatedAt
  };
};

export const getResources = async (filters: {
  type?: string;
  level?: string;
  theme?: string;
  chapter?: string;
  searchQuery?: string;
}): Promise<Resource[]> => {
  try {
    console.log("Fetching resources with filters:", filters);
    
    // Créer la requête de base
    let queryRef = collection(db, 'resources');
    let constraints: any[] = [];

    // Ajouter les filtres valides uniquement
    if (filters.type && filters.type !== 'all') {
      constraints.push(where('type', '==', filters.type));
    }
    if (filters.level && filters.level !== 'all') {
      constraints.push(where('level', '==', filters.level));
    }
    if (filters.theme && filters.theme !== 'all') {
      constraints.push(where('theme', '==', filters.theme));
    }
    if (filters.chapter && filters.chapter !== 'all') {
      constraints.push(where('chapter', '==', filters.chapter));
    }

    // Ajouter le tri par date à la fin
    constraints.push(orderBy('createdAt', 'desc'));
    constraints.push(limit(50));

    // Construire la requête finale
    let q = query(queryRef, ...constraints);

    // Exécuter la requête
    const querySnapshot = await getDocs(q);
    console.log(`Found ${querySnapshot.docs.length} resources matching filters`);
    
    let resources = querySnapshot.docs.map(convertResource);

    // Appliquer le filtre de recherche si nécessaire
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery.toLowerCase().trim();
      resources = resources.filter(resource => 
        resource.title.toLowerCase().includes(searchLower) ||
        resource.description.toLowerCase().includes(searchLower) ||
        (resource.chapter && resource.chapter.toLowerCase().includes(searchLower))
      );
    }

    return resources;
  } catch (error) {
    console.error('Error fetching resources:', error);
    // En cas d'erreur, retourner un tableau vide plutôt que de propager l'erreur
    return [];
  }
};

export const getPdfUrl = async (pdfPath: string): Promise<string> => {
  try {
    console.log("Getting PDF URL for path:", pdfPath);
    
    // Vérifier que le chemin du PDF est valide
    if (!pdfPath || typeof pdfPath !== 'string') {
      throw new Error('Chemin du PDF invalide');
    }

    // Nettoyer le chemin
    const cleanPath = pdfPath.trim();
    if (!cleanPath) {
      throw new Error('Chemin du PDF vide');
    }

    // Si c'est déjà une URL complète, la retourner directement
    if (cleanPath.startsWith('http')) {
      return cleanPath;
    }

    // Créer la référence au fichier dans le storage
    const fileRef = ref(storage, cleanPath);
    
    // Obtenir l'URL de téléchargement
    const downloadUrl = await getDownloadURL(fileRef);
    console.log("Generated download URL:", downloadUrl);
    
    return downloadUrl;
  } catch (error: any) {
    console.error('Error getting PDF URL:', error);
    if (error.code === 'storage/object-not-found') {
      throw new Error('Le fichier n\'est pas disponible pour le moment');
    }
    if (error.code === 'storage/invalid-argument') {
      throw new Error('Chemin du fichier invalide');
    }
    throw new Error('Erreur lors du téléchargement du PDF');
  }
};

// Fonction pour télécharger un PDF de manière compatible avec Safari
export const downloadPdf = async (pdfPath: string, fileName?: string): Promise<void> => {
  try {
    // Obtenir l'URL du PDF
    const url = await getPdfUrl(pdfPath);
    
    // Déterminer le nom du fichier
    const defaultFileName = pdfPath.split('/').pop() || 'document.pdf';
    const finalFileName = fileName || defaultFileName;
    
    // Créer un élément a temporaire pour le téléchargement
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', finalFileName);
    link.setAttribute('target', '_blank');
    link.style.display = 'none';
    document.body.appendChild(link);
    
    // Simuler un clic sur le lien
    link.click();
    
    // Pour Safari, on ajoute un délai avant de supprimer le lien
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
    
    // Méthode alternative pour Safari
    if (navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1) {
      window.location.href = url;
    }
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};

export const getResourceById = async (resourceId: string): Promise<Resource | null> => {
  try {
    const docRef = doc(db, 'resources', resourceId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Convertir les timestamps Firestore en objets Date
      let createdAt = new Date();
      if (data.createdAt) {
        if (data.createdAt instanceof Timestamp) {
          createdAt = data.createdAt.toDate();
        } else if (data.createdAt.seconds) {
          createdAt = new Date(data.createdAt.seconds * 1000);
        }
      }
      
      let updatedAt = new Date();
      if (data.updatedAt) {
        if (data.updatedAt instanceof Timestamp) {
          updatedAt = data.updatedAt.toDate();
        } else if (data.updatedAt.seconds) {
          updatedAt = new Date(data.updatedAt.seconds * 1000);
        }
      }
      
      return {
        id: docSnap.id,
        title: data.title || 'Sans titre',
        description: data.description || '',
        type: data.type || 'fiche',
        level: data.level || 'terminale',
        theme: data.theme || 'economie',
        chapter: data.chapter || '',
        pdfUrl: data.pdfUrl || '',
        downloads: data.downloads || 0,
        createdAt,
        updatedAt
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting resource by ID:', error);
    return null;
  }
};